import { jsx as _jsx } from "react/jsx-runtime";
// ThemeProvider — корень визуального языка. Принимает выбранную тему ('light'|'dark'|'system'),
// разрешает её в конкретную ('system' → prefers-color-scheme, на лету при смене медиа-запроса),
// вешает на свой корневой <div> набор CSS custom properties (themeCssVars) + data-theme. Дочерние
// компоненты ссылаются только на var(--…) и наследуют тему через DOM-каскад — смена темы НЕ требует
// перерендера данных (custom properties пересчитываются браузером).
//
// Презентационность (инв.4): провайдер держит только UI-состояние (resolved-тема) и подписку на
// media-query. Никакой сети/доменного стора.
import { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { themeCssVars } from '../tokens/index.js';
import { ensureGlobalStyles, ensureViewportHeight } from '../styles.js';
const ThemeContext = createContext(null);
/** Прочитать текущий prefers-color-scheme (SSR-safe: вне браузера → 'light'). */
function readSystemTheme() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Оборачивает поддерево, задавая ему тему. Для 'system' подписывается на изменение
 * prefers-color-scheme и переключается без перемонтирования детей.
 */
export function ThemeProvider({ theme = 'system', children, className, style, }) {
    const [systemTheme, setSystemTheme] = useState(readSystemTheme);
    // Корневой ли это провайдер (нет родительского ThemeContext выше по дереву).
    const parentTheme = useContext(ThemeContext);
    const isRootProvider = parentTheme === null;
    // Глобальные keyframes кита + document-reset — однократно (анимации typing/sheet/push и т.п.,
    // и height-цепочка html→body→#root, чтобы тема рисовала фон на всю высоту вьюпорта).
    useEffect(ensureGlobalStyles, []);
    // Привязать высоту приложения к visualViewport: экранная клавиатура сжимает ленту, а не проматывает
    // документ (шапка чата остаётся на месте). No-op без visualViewport (остаётся CSS-fallback 100%).
    useEffect(ensureViewportHeight, []);
    useEffect(() => {
        if (theme !== 'system')
            return;
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => setSystemTheme(mq.matches ? 'dark' : 'light');
        // синхронизируемся сразу (медиа-запрос мог измениться между рендером и эффектом)
        onChange();
        // addEventListener поддерживается современными браузерами; addListener — легаси-фолбэк
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, [theme]);
    const resolved = theme === 'system' ? systemTheme : theme;
    const ctx = useMemo(() => ({ preference: theme, resolved }), [theme, resolved]);
    // CSS-vars темы как inline-стиль корня + data-theme для семантического хука стилей/тестов.
    const vars = useMemo(() => themeCssVars(resolved), [resolved]);
    // Корневой провайдер зеркалит фон темы и color-scheme на <html>: при overscroll/rubber-band и на
    // нативных скроллбарах/контролах виден сплошной фон темы (а не белый body), на любом размере экрана.
    // Нестящиеся провайдеры документ не трогают (фон даёт их собственный data-ds-root).
    useEffect(() => {
        if (!isRootProvider || typeof document === 'undefined')
            return;
        const root = document.documentElement;
        const prevBg = root.style.backgroundColor;
        const prevScheme = root.style.colorScheme;
        root.style.backgroundColor = vars['--color-bg'] ?? '';
        root.style.colorScheme = resolved;
        return () => {
            root.style.backgroundColor = prevBg;
            root.style.colorScheme = prevScheme;
        };
    }, [isRootProvider, vars, resolved]);
    const rootStyle = {
        ...vars,
        color: 'var(--color-text)',
        background: 'var(--color-bg)',
        fontFamily: 'var(--font-family-base)',
        ...style,
    };
    return (_jsx(ThemeContext.Provider, { value: ctx, children: _jsx("div", { className: className, "data-theme": resolved, "data-ds-root": "", style: rootStyle, children: children }) }));
}
/** Текущая тема (preference + resolved). Бросает вне ThemeProvider — явная ошибка композиции. */
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (ctx === null) {
        throw new Error('useTheme(): компонент должен быть внутри <ThemeProvider>');
    }
    return ctx;
}
//# sourceMappingURL=ThemeProvider.js.map